import type { WorkbookOptions, WorkbookSheet, WorkbookSheetRowCell } from '@progress/kendo-ooxml';
import moment from 'moment';
import { PerfectOrder, PerfectOrderFileResponse } from 'src/types/perfectOrder';
import { isSpecialItem } from 'src/utils/perfectOrderRules';

type CellValue = string | number | boolean | null | undefined;

export interface BuildPerfectOrderWorkbookOptions {
  combinedOrderNumber: string | number | undefined;
  formatPhone: (value?: string) => string | undefined;
  getDropdownLabel: (type: 'Status' | 'RootCause' | 'AssignedTo', value?: number) => string;
  getClaimTypeLabel: (value?: number) => string;
  getReturnReasonLabel: (value?: number) => string;
}

const INVALID_XML_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

const headerCell: WorkbookSheetRowCell = {
  bold: true,
  background: '#f5f5f5',
};

const formatExportDate = (value?: string | null, includeTime = false): string => {
  if (!value) {
    return '';
  }

  const date = includeTime ? moment(value) : moment.parseZone(value);
  return date.format(includeTime ? 'M/DD/YYYY h:mm A' : 'M/DD/YYYY');
};

const getFileName = (file: PerfectOrderFileResponse): string => {
  if (file.fileName) {
    return file.fileName;
  }

  if (!file.fileUrl) {
    return 'Attachment';
  }

  try {
    const url = new URL(file.fileUrl, window.location.origin);
    const pathName = url.pathname.split('/').filter(Boolean).pop();
    return pathName ? decodeURIComponent(pathName) : 'Attachment';
  } catch {
    const pathName = file.fileUrl.split('/').filter(Boolean).pop();
    return pathName ? decodeURIComponent(pathName.split('?')[0]) : 'Attachment';
  }
};

const sanitizeCellValue = (value: CellValue): string | number | boolean => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.replace(INVALID_XML_CHARS, '');
  }
  if (typeof value === 'number' && !Number.isFinite(value)) {
    return '';
  }
  return value;
};

const createSheet = (name: string, headers: string[], rows: CellValue[][]): WorkbookSheet => ({
  name,
  frozenRows: 1,
  columns: headers.map(() => ({ autoWidth: true })),
  rows: [
    {
      cells: headers.map((header) => ({ ...headerCell, value: header })),
    },
    ...rows.map((row) => ({
      cells: row.map((value) => ({ value: sanitizeCellValue(value) })),
    })),
  ],
});

export const buildPerfectOrderWorkbook = (order: PerfectOrder, options: BuildPerfectOrderWorkbookOptions): WorkbookOptions => {
  const { combinedOrderNumber, formatPhone, getDropdownLabel, getClaimTypeLabel, getReturnReasonLabel } = options;

  const detailsRows: Array<[string, CellValue]> = [
    ['Perfect Order ID', order.perfectOrderId],
    ['Order Number', combinedOrderNumber],
    ['Customer Number', order.customerNumber],
    ['Ship To', order.shipTo],
    ['Warehouse', order.warehouse],
    ['Backup Warehouse', order.backupWarehouse],
    ['Customer Contact Name', order.customerContactName],
    ['Customer Contact Phone', formatPhone(order.customerContactPhone)],
    ['Customer Contact Email', order.customerContactEmail],
    ['Requester Email', order.requesterEmail],
    ['Source', order.source],
    ['Comment', order.requesterComment],
    ['Date Opened', formatExportDate(order.dateOpened)],
    ['Date Closed', formatExportDate(order.dateClosed)],
    ['Date Shipped', formatExportDate(order.shippedDate)],
    ['Invoice Date', formatExportDate(order.invoicedDate)],
    ['Total Amount', order.totalAmount],
    ['Status', getDropdownLabel('Status', order.statusId)],
    ['Claim Type', getClaimTypeLabel(order.claimTypeId)],
    ['Return Reason', getReturnReasonLabel(order.returnReasonId)],
    ['Assigned To', getDropdownLabel('AssignedTo', order.assignedTo)],
    ['Buyer', order.buyer],
    ['Root Cause', getDropdownLabel('RootCause', order.rootCauseId)],
    ['DNS Needed', order.isDnsNeeded],
    ['DNS Number', order.dnsNumber],
    ['Credit Needed', order.isCreditNeeded],
    ['Credit Number', order.creditNumber],
    ['Reason For Credit ID', order.reasonForCreditId],
  ];

  const itemRows = [...(order.perfectOrderItems || [])]
    .sort((a, b) => (a.perfectOrderLineId ?? 0) - (b.perfectOrderLineId ?? 0))
    .map((item) => [
      item.retOrderLine,
      item.vendorNumber,
      item.itemNumber,
      item.description,
      item.upc,
      item.quantity,
      item.quantityUnit,
      item.customerPrice,
      item.stockDesignationId,
      isSpecialItem(item) ? 'Return to Warehouse' : '',
    ]);

  const noteRows = [...(order.perfectOrderNotes || [])]
    .sort((a, b) => {
      const aDate = a.createDate ? new Date(a.createDate).getTime() : 0;
      const bDate = b.createDate ? new Date(b.createDate).getTime() : 0;
      return bDate - aDate;
    })
    .map((note) => [formatExportDate(note.createDate, true), note.createdBy, note.note]);

  const attachmentRows = [...(order.perfectOrderFiles || [])]
    .sort((a, b) => (a.perfectOrderFileId ?? 0) - (b.perfectOrderFileId ?? 0))
    .map((file) => [getFileName(file), file.fileUrl, file.contentType, file.fileSize, file.createdBy, formatExportDate(file.createDate, true)]);

  return {
    creator: 'SFA Web',
    date: new Date(),
    sheets: [
      createSheet('Perfect Order', ['Field', 'Value'], detailsRows),
      createSheet('Items', ['Order Line', 'Vendor #', 'Item #', 'Description', 'UPC', 'Qty', 'Qty Unit', 'Customer Price', 'Stock Designation ID', 'Status'], itemRows),
      createSheet('Notes', ['Created Date', 'Created By', 'Note'], noteRows),
      createSheet('Attachments', ['File Name', 'URL', 'Content Type', 'File Size', 'Created By', 'Created Date'], attachmentRows),
    ],
  };
};
